import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FaPlus } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { TBaseAdminData } from 'views/types/admins';
import { Button } from 'components/button';
import { adminService } from 'views/services/adminService';
import { useDebounceValue } from 'usehooks-ts';
import { AdminsFilterDropdown } from '../AdminsFilterDropdown/AdminsFilterDropdown';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';
import { useAdminPermissions } from 'hooks/useAdminPermissions';
import { useAdminsStore } from 'views/store/useAdminsStore';
import { toast } from 'App';

export const CreateAdminPopup = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [roles, setRoles] = useState<
    { id: string; name: string; description: string }[]
  >([]);

  const [searchDebounce] = useDebounceValue(search, 1000);
  const { canCreate, canUpdate } = usePermissions();
  const { getAvailableRoles } = useAdminPermissions();
  const { currentAdmin } = useAdminsStore();

  const availableRoles = getAvailableRoles(currentAdmin?.role || '', roles);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<TBaseAdminData>({
    defaultValues: {
      login: '',
      password: '',
      role: '',
      name: '',
      telegramId: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: TBaseAdminData) => {
    if (
      !canCreate(PERMISSION_RESOURCES.ADMINS) ||
      !canCreate(PERMISSION_RESOURCES.CREATE_ADMIN)
    ) {
      toast({
        title: 'Error',
        description: 'You do not have permission to create admin accounts.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    await adminService.createAdmin(data);
    setOpen(false);
    reset({
      login: '',
      password: '',
      role: availableRoles[0]?.name || '',
      name: '',
      telegramId: '',
    });
  };

  useEffect(() => {
    const normalizedSearch = searchDebounce
      .replace(/\bactive\b/i, 'on')
      .replace(/\binactive\b/i, 'off');
    adminService.getAll(normalizedSearch);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounce]);

  useEffect(() => {
    const loadRoles = async () => {
      const rolesData = await adminService.getRoles();
      setRoles(rolesData);
    };
    loadRoles();
  }, []);

  useEffect(() => {
    if (availableRoles.length > 0) {
      setValue('role', availableRoles[0].name);
    }
  }, [availableRoles, setValue]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <div className="flex justify-between pr-4">
        <ReadPermissionGate resource={PERMISSION_RESOURCES.CREATE_ADMIN}>
          <Dialog.Trigger asChild>
            <Button
              disabled={
                !canUpdate(PERMISSION_RESOURCES.ADMINS) ||
                !canUpdate(PERMISSION_RESOURCES.CREATE_ADMIN)
              }
            >
              <FaPlus className="mr-2" /> Create Admin
            </Button>
          </Dialog.Trigger>
        </ReadPermissionGate>

        <div className="flex justify-between items-center mb-4 px-4">
          <ReadPermissionGate resource={PERMISSION_RESOURCES.FILTER_ADMINS}>
            <AdminsFilterDropdown
              disabled={
                !canUpdate(PERMISSION_RESOURCES.ADMINS) ||
                !canUpdate(PERMISSION_RESOURCES.FILTER_ADMINS)
              }
            />
          </ReadPermissionGate>
          <ReadPermissionGate resource={PERMISSION_RESOURCES.FILTER_ADMINS}>
            <input
              type="text"
              placeholder="Search by Role, Status or active"
              className="border h-9 rounded-md px-3 text-base text-navy-700 dark:text-white dark:bg-navy-900 placeholder:text-gray-400 dark:placeholder:text-white w-[250px] max-w-full disabled:opacity-50 disabled:cursor-not-allowed"
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={
                !canUpdate(PERMISSION_RESOURCES.ADMINS) ||
                !canUpdate(PERMISSION_RESOURCES.FILTER_ADMINS)
              }
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              autoCapitalize="none"
            />
          </ReadPermissionGate>
        </div>
      </div>

      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
        <Dialog.Content
          onInteractOutside={e => {
            if ((e.target as HTMLElement).closest('.chakra-toast')) {
              e.preventDefault();
            }
          }}
          className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[60] max-h-[85vh] h-[85vh] w-[90vw] max-w-[90vw] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white p-[25px] shadow-lg focus:outline-none dark:bg-gray-800 dark:text-white overflow-y-auto"
        >
          <Dialog.Title className="text-center text-lg font-medium mb-6">
            Create Admin
          </Dialog.Title>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block mb-1">Login (email)</label>
              <input
                type="email"
                {...register('login', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                })}
                className="dark:bg-[transparent] w-full rounded border px-3 py-2"
              />
              {errors.login && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.login.message}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-1">Name</label>
              <input
                type="text"
                {...register('name', {
                  required: 'Name is required',
                  maxLength: {
                    value: 20,
                    message: 'Maximum 20 characters',
                  },
                })}
                className={`dark:bg-[transparent] w-full rounded border px-3 py-2 ${errors.name ? 'border-red-500' : ''}`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-1">Telegram ID</label>
              <input
                type="text"
                {...register('telegramId', {
                  required: 'Telegram ID is required',
                  maxLength: {
                    value: 20,
                    message: 'Maximum 20 characters',
                  },
                  pattern: {
                    value: /^\d*$/,
                    message: 'Numbers only',
                  },
                })}
                className={`dark:bg-[transparent] w-full rounded border px-3 py-2 ${errors.telegramId ? 'border-red-500' : ''}`}
              />
              {errors.telegramId && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.telegramId.message}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-1">Password</label>
              <input
                type="password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters long',
                  },
                  pattern: {
                    value:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#@$!%*?&]).{8,}$/,
                    message:
                      'Must include uppercase, lowercase, number and special character',
                  },
                })}
                className="dark:bg-[transparent] w-full rounded border px-3 py-2"
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-1">Role</label>
              <select
                {...register('role')}
                className="dark:bg-gray-800 w-full rounded border px-3 py-2"
              >
                {availableRoles.map(role => (
                  <option
                    className="dark:bg-gray-800"
                    key={role.id}
                    value={role.name}
                  >
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-center pt-4">
              <Button type="submit">Create</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
